// frontend/src/components/common/CreditPurchaseModal.js
import React, { useState, useEffect } from 'react';
import { FaTimes, FaGem, FaSpinner, FaCheckCircle } from 'react-icons/fa';
import { premiumApi } from '../../api/premium';
import { settingsApi } from '../../api/settings';
import helpers from '../../utils/helpers';

function CreditPurchaseModal({ isOpen, onClose, type = 'listing' }) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({});
  const [processing, setProcessing] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchConfig();
    }
  }, [isOpen]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const settings = await settingsApi.getAllSettings();
      setConfig(settings.premium_config || {});
    } catch (err) {
      console.error("Failed to load pricing", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!selectedPack) return;
    setProcessing(true);
    try {
      const response = await premiumApi.buyCredits(type, selectedPack);
      if (response.payment_link) {
        window.location.href = response.payment_link;
      } else {
        alert("Failed to initialize payment.");
        setProcessing(false);
      }
    } catch (err) {
      alert("Payment Error: " + (err.message || "Unknown error"));
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  // Define packs based on type
  const packs = type === 'listing' ? [1, 5, 10, 20, 50] : [1, 10, 50];
  const title = type === 'listing' ? 'Listing Credits' : 'AI Description Credits';
  const description = type === 'listing' 
    ? 'Purchase credits to upload more images (up to 12) per vehicle listing.' 
    : 'Purchase credits to generate professional AI descriptions for your items.';

  return (
    // Increased z-index to 10000 to be above the ScrollToTop button (z-9999)
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-80 p-4 backdrop-blur-sm">
      
      {/* Reduced max-height to 85vh to ensure clearance on mobile browsers */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden animate-fadeInUp relative">
        
        <button 
            onClick={onClose} 
            className="absolute top-4 right-4 text-white hover:text-gray-200 z-20 bg-black/20 rounded-full p-1"
        >
            <FaTimes size={20} />
        </button>

        {loading ? (
            <div className="p-12 text-center">
                <FaSpinner className="animate-spin text-4xl text-purple-600 mx-auto mb-4" />
                <p className="text-gray-500">Loading packages...</p>
            </div>
        ) : (
            <div className="flex flex-col h-full overflow-hidden">
                
                {/* HEADER - Fixed at top (flex-shrink-0) */}
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 sm:p-8 text-center text-white flex-shrink-0">
                    <FaGem className="text-4xl sm:text-5xl mx-auto mb-3 text-yellow-300 drop-shadow-md animate-pulse" />
                    <h2 className="text-xl sm:text-2xl font-bold mb-1">Get {title}</h2>
                    {/* Increased font size to text-sm */}
                    <p className="text-purple-100 text-sm px-2 leading-tight">
                        {description}
                    </p>
                </div>

                {/* CONTENT - Scrollable Area (flex-grow) */}
                <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-grow bg-gray-50">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Select a Package</h4>
                    
                    <div className="space-y-3 pb-2">
                        {packs.map(pack => {
                            const priceKey = type === 'listing' ? `listing_credit_price_${pack}` : `ai_desc_price_${pack}`;
                            const price = config[priceKey];
                            if (!price) return null;

                            return (
                                <div 
                                    key={pack}
                                    onClick={() => setSelectedPack(pack)}
                                    className={`flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 border-2 rounded-xl cursor-pointer transition-all gap-1 ${
                                        selectedPack === pack 
                                        ? 'border-purple-500 bg-white ring-2 ring-purple-500 shadow-md' 
                                        : 'border-gray-200 bg-white hover:border-purple-300'
                                    }`}
                                >
                                    <div>
                                        <span className="font-bold text-gray-800 text-base">{pack} Credit{pack > 1 ? 's' : ''}</span>
                                        {/* Increased font size to text-xs (was text-[10px]) */}
                                        {type === 'listing' && <span className="block text-xs text-gray-500 mt-0.5">Upload up to 12 images per credit</span>}
                                    </div>
                                    <div className="text-purple-700 font-black text-lg self-end sm:self-auto">
                                        {helpers.formatCurrency(price, 'NGN')}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* FOOTER - Fixed at bottom (flex-shrink-0) */}
                <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0 z-10 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
                    <button 
                        onClick={handlePurchase}
                        disabled={processing || !selectedPack}
                        className={`w-full py-3 rounded-xl font-black text-white shadow-lg transition-all flex items-center justify-center gap-2 ${
                            !selectedPack
                            ? 'bg-gray-300 cursor-not-allowed' // Disabled state (Gray)
                            : processing
                                ? 'bg-purple-400 cursor-wait' // Processing state (Light Purple - Visible)
                                : 'bg-purple-600 hover:bg-purple-700 hover:scale-[1.02] active:scale-95' // Active state
                        }`}
                    >
                        {processing ? (
                            <>
                                <FaSpinner className="animate-spin" /> Redirecting...
                            </>
                        ) : (
                            <>
                                <FaCheckCircle /> Purchase Now
                            </>
                        )}
                    </button>
                </div>
            </div>
        )}
      </div>
    </div>
  );
}

export default CreditPurchaseModal;